import React, { useEffect, useState, useCallback } from 'react';
import { useTrainingMode } from './TrainingModeContext';

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PADDING = 6;

export function TrainingSpotlight() {
  const { isActive, currentStep } = useTrainingMode();
  const [rect, setRect] = useState<Rect | null>(null);

  const measure = useCallback(() => {
    if (!isActive || !currentStep?.targetSelector) {
      setRect(null);
      return;
    }
    const el = document.querySelector(`[data-training-id="${currentStep.targetSelector}"]`);
    if (!el) {
      setRect(null);
      return;
    }
    const box = el.getBoundingClientRect();
    if (box.width === 0 || box.height === 0) {
      // Element exists in the DOM but isn't visible (e.g. hidden below a
      // responsive breakpoint) — treat the same as "not found" rather than
      // spotlighting a collapsed box.
      setRect(null);
      return;
    }
    setRect({
      top: box.top - PADDING,
      left: box.left - PADDING,
      width: box.width + PADDING * 2,
      height: box.height + PADDING * 2,
    });
  }, [isActive, currentStep]);

  useEffect(() => {
    measure();
    // Retry with backoff after mount/navigation in case the target renders
    // async (data fetch, lazy-loaded chunk, tab switch animation).
    const retries = [200, 500, 1000, 2000].map(delay => setTimeout(measure, delay));
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      retries.forEach(clearTimeout);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [measure]);

  if (!isActive || !rect) return null;

  return (
    <div className="fixed inset-0 z-[65] pointer-events-none">
      <div
        className="absolute rounded-xl ring-4 ring-amber-400 ring-offset-2 transition-all duration-200"
        style={{
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.35)',
        }}
      />
    </div>
  );
}
